import React, { useRef, useState } from 'react';
import { Row, Col, Form, Button, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Timestamp } from 'firebase/firestore';
import { MdFlight, MdPlace, MdAttachFile, MdAdd, MdClose } from 'react-icons/md';
import { createArchive, selectCreateStatus } from '_redux/slices/archive/archive';
import { selectFirebaseUser } from '_redux/slices/main-screen/user';
import { uploadAttachment, appendAttachments } from '_services/archive-service';
import { Colors } from '_styles';

const sectionCard = {
    background: '#fff',
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    borderRadius: 12,
    padding: '24px 28px',
    marginBottom: 20,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

const sectionHeader = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 20,
};

const sectionTitle = {
    fontSize: 16,
    fontWeight: 600,
    color: Colors.TEXT_COLOR,
    margin: 0,
};

const stepBadge = {
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: Colors.PRM_LIGHT_BLUE,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 600,
    flexShrink: 0,
};

const submitButton = {
    padding: '12px 40px',
    fontSize: 15,
    fontWeight: 600,
    borderRadius: 8,
    border: 'none',
    backgroundColor: Colors.PRM_LIGHT_BLUE,
    color: '#fff',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    boxShadow: '0 2px 8px rgba(48, 127, 226, 0.3)',
};

const errorBanner = {
    marginTop: 12,
    padding: '10px 14px',
    background: Colors.LIGHT_RED,
    borderRadius: 8,
    borderLeft: `4px solid ${Colors.DARK_RED}`,
};

const errorText = {
    color: Colors.DARK_RED,
    margin: 0,
    fontSize: 14,
    fontWeight: 500,
};

const LABEL_STYLE = {
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 4,
};

const REQUIRED_STAR = { color: Colors.DARK_RED, marginLeft: 2 };



// Restrict airport codes to 3 uppercase letters as the user types.
const sanitizeAirportCode = (value) => (value || '').replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 3);

const buildCreatorObject = (dbUser) => {
    if (!dbUser) return { id: 'unknown', name: 'Unknown User', email: 'Unknown User' };
    const id = dbUser.uid || 'unknown';
    const displayName = dbUser.displayName || 'Unknown User';
    const email = dbUser.email || 'Unknown User';
    return { id, name: displayName, email };
};

const ArchiveForm = ({ onSuccess, onCancel }) => {
    const dispatch = useDispatch();
    const dbUser = useSelector(selectFirebaseUser);
    const createStatus = useSelector(selectCreateStatus);
    const [flightNumber, setFlightNumber] = useState('');
    const [origin, setOrigin] = useState('');
    const [destination, setDestination] = useState('');
    const [flightDate, setFlightDate] = useState('');
    const [files, setFiles] = useState([]);
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState(null);
    const fileInputRef = useRef(null);

    const addFiles = (newList) => {
        const incoming = Array.from(newList || []);
        if (incoming.length === 0) return;
        setFiles((prev) => {
            const existingKeys = new Set(prev.map((f) => `${f.name}-${f.size}-${f.lastModified}`));
            const deduped = incoming.filter(
                (f) => !existingKeys.has(`${f.name}-${f.size}-${f.lastModified}`)
            );
            return [...prev, ...deduped];
        });
    };

    const removeFile = (idx) => {
        setFiles((prev) => prev.filter((_, i) => i !== idx));
    };

    const validate = () => {
        const e = {};
        if (!flightNumber.trim()) e.flightNumber = 'Flight number is required.';
        if (!origin.trim()) e.origin = 'Origin is required.';
        else if (!/^[A-Z]{3}$/.test(origin)) e.origin = 'Origin must be a 3-letter code.';
        if (!destination.trim()) e.destination = 'Destination is required.';
        else if (!/^[A-Z]{3}$/.test(destination)) e.destination = 'Destination must be a 3-letter code.';
        if (!flightDate) e.flightDate = 'Flight date is required.';
        return e;
    };

    const handleSubmit = async (evt) => {
        evt.preventDefault();
        setSubmitError(null);

        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setErrors({});

        const iata = dbUser?.details?.authority;
        const creator = buildCreatorObject(dbUser);

        const archiveData = {
            flightNumber: flightNumber.trim().toUpperCase(),
            origin: origin.trim().toUpperCase(),
            destination: destination.trim().toUpperCase(),
            flightDate: Timestamp.fromDate(new Date(flightDate)),
            iata,
            createdBy: creator,
            attachments: [],
        };

        const result = await dispatch(createArchive(archiveData));

        if (result.meta?.requestStatus === 'rejected') {
            setSubmitError(result.error?.message ?? 'Failed to create archive. Please try again.');
            return;
        }

        const archiveId = result.payload;

        if (files.length > 0 && archiveId) {
            setUploadingFiles(true);
            const uploaded = [];
            for (const file of files) {
                try {
                    const meta = await uploadAttachment(archiveId, file, creator);
                    uploaded.push(meta);
                } catch (err) {
                    console.error('Failed to upload attachment', file.name, err);
                }
            }
            if (uploaded.length > 0) {
                await appendAttachments(archiveId, uploaded, []);
            }
            setUploadingFiles(false);
        }

        onSuccess?.();
    };

    const isSubmitting = createStatus === 'loading' || uploadingFiles;

    return (
        <Form onSubmit={handleSubmit} style={{ width: '100%', marginTop: 8 }} noValidate>

            <div style={sectionCard}>
                <div style={sectionHeader}>
                    <div style={stepBadge}>1</div>
                    <MdFlight size={18} color={Colors.PRM_LIGHT_BLUE} />
                    <h3 style={sectionTitle}>Flight Details</h3>
                </div>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label style={LABEL_STYLE}>
                                Flight Number <span style={REQUIRED_STAR}>*</span>
                            </Form.Label>
                            <Form.Control
                                placeholder="e.g. EI401"
                                value={flightNumber}
                                onChange={(e) => setFlightNumber(e.target.value)}
                                isInvalid={!!errors.flightNumber}
                                style={{ borderRadius: 8, fontSize: 14 }}
                            />
                            <Form.Control.Feedback type="invalid">{errors.flightNumber}</Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label style={LABEL_STYLE}>
                                Flight Date <span style={REQUIRED_STAR}>*</span>
                            </Form.Label>
                            <Form.Control
                                type="date"
                                value={flightDate}
                                onChange={(e) => setFlightDate(e.target.value)}
                                isInvalid={!!errors.flightDate}
                                style={{ borderRadius: 8, fontSize: 14 }}
                            />
                            <Form.Control.Feedback type="invalid">{errors.flightDate}</Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>
            </div>

            <div style={sectionCard}>
                <div style={sectionHeader}>
                    <div style={stepBadge}>2</div>
                    <MdPlace size={18} color={Colors.PRM_LIGHT_BLUE} />
                    <h3 style={sectionTitle}>Route</h3>
                </div>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label style={LABEL_STYLE}>
                                Origin <span style={REQUIRED_STAR}>*</span>
                            </Form.Label>
                            <Form.Control
                                placeholder="e.g. DUB"
                                value={origin}
                                onChange={(e) => setOrigin(sanitizeAirportCode(e.target.value))}
                                isInvalid={!!errors.origin}
                                maxLength={3}
                                style={{ borderRadius: 8, fontSize: 14, textTransform: 'uppercase' }}
                            />
                            <Form.Control.Feedback type="invalid">{errors.origin}</Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label style={LABEL_STYLE}>
                                Destination <span style={REQUIRED_STAR}>*</span>
                            </Form.Label>
                            <Form.Control
                                placeholder="e.g. JFK"
                                value={destination}
                                onChange={(e) => setDestination(sanitizeAirportCode(e.target.value))}
                                isInvalid={!!errors.destination}
                                maxLength={3}
                                style={{ borderRadius: 8, fontSize: 14, textTransform: 'uppercase' }}
                            />
                            <Form.Control.Feedback type="invalid">{errors.destination}</Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>
            </div>

            <div style={{
                ...sectionCard,
                background: Colors.GRAY_LIGHT,
                border: `1px dashed #d1d5db`,
            }}>
                <div style={sectionHeader}>
                    <div style={{ ...stepBadge, backgroundColor: '#6b7280' }}>3</div>
                    <MdAttachFile size={18} color="#6b7280" />
                    <h3 style={sectionTitle}>Attachments</h3>
                    <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 400, marginLeft: 4 }}>Optional</span>
                </div>

                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => {
                        addFiles(e.target.files);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                />
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '8px 16px',
                        border: `1px solid ${Colors.PRM_LIGHT_BLUE}`,
                        borderRadius: 8,
                        background: '#fff',
                        color: Colors.PRM_LIGHT_BLUE,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                    }}
                >
                    <MdAdd size={16} />
                    {files.length > 0 ? 'Add More Files' : 'Add Files'}
                </button>

                {files.length > 0 && (
                    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {files.map((f, idx) => (
                            <div
                                key={`${f.name}-${f.size}-${f.lastModified}-${idx}`}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '6px 12px',
                                    background: '#fff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 6,
                                    fontSize: 13,
                                    color: '#374151',
                                }}
                            >
                                <MdAttachFile size={14} color="#6b7280" />
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {f.name}
                                </span>
                                <span style={{ color: '#9ca3af', fontSize: 12 }}>
                                    {(f.size / 1024).toFixed(1)} KB
                                </span>
                                <button
                                    type="button"
                                    onClick={() => removeFile(idx)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: 4,
                                        border: 'none',
                                        background: 'transparent',
                                        color: Colors.DARK_RED,
                                        cursor: 'pointer',
                                    }}
                                    aria-label={`Remove ${f.name}`}
                                >
                                    <MdClose size={16} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div style={{ marginTop: 8, paddingTop: 4, paddingBottom: 40 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button
                        type="submit"
                        style={{
                            ...submitButton,
                            opacity: isSubmitting ? 0.7 : 1,
                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        }}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Spinner animation="border" size="sm" />
                                {uploadingFiles ? 'Uploading files…' : 'Creating archive…'}
                            </span>
                        ) : 'Create Archive'}
                    </button>
                    {onCancel && (
                        <Button
                            variant="outline-secondary"
                            onClick={onCancel}
                            style={{ borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 500 }}
                        >
                            Cancel
                        </Button>
                    )}
                </div>

                {submitError && (
                    <div style={errorBanner}>
                        <p style={errorText}>{submitError}</p>
                    </div>
                )}
            </div>
        </Form>
    );
};

export default ArchiveForm;
